import type { AgendaItem } from "@repo/types";
import { Button } from "@repo/ui/components/Base/Button";
import { DatePicker } from "@repo/ui/components/Base/DatePicker";
import { WeekdayPicker } from "@repo/ui/components/Base/WeekdayPicker";
import { MonthPicker } from "@repo/ui/components/Base/MonthPicker";
import { Input } from "@repo/ui/components/Base/Input";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import { Formik } from "formik";
import { useState } from "react";
import * as yup from "yup";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@repo/ui/components/Base/Dialog";
import {
  type RecurrenceSettings,
  computeReoccurrences,
  intervalNames,
  intervalOptions,
  tweakSelections,
  weekdayIndexNames,
  weekdayIndexOptions,
  weekdays,
} from "@repo/shared/recurring";
import { wholeNumber } from "@repo/shared/utils";
import { CheckboxInput } from "@repo/ui/components/Base/CheckboxInput";
import { SimpleDate } from "@repo/lib/simpleDate";
import { ReoccurrencePreview } from "../common/ReoccurrencePreview";
import { useTrackedNewObjects } from "@/hooks/trackCreatedObjects";
import { useUserStore } from "@/stores/userStore";
import { useBlockUnload } from "@repo/ui/components/Base/hooks/useBlockUnload";
import { agendaTemplateService } from "@/services/agendaTemplateService";
import { agendaItemRepo } from "@/repositories/agendaItemRepo";

const maxRecurrences = 100;

export interface ReoccurringAgendaItemFormProps {
  tenantId: string;
  agendaItem: AgendaItem;
  handleCancel: () => void;
  handleComplete: () => void;
  withinDialog?: boolean;
}

export const ReoccurringAgendaItemForm = ({
  tenantId,
  agendaItem,
  handleCancel,
  handleComplete,
  withinDialog,
}: ReoccurringAgendaItemFormProps) => {
  useBlockUnload();

  const team = useUserStore((s) => s.activeTeam);

  const validationSchema = yup.object({});

  const trackNew = useTrackedNewObjects();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submission, setSubmission] =
    useState<React.FormEvent<HTMLFormElement>>();

  const skipDate =
    agendaItem.requestedApprovalDate && agendaItem.requestedApprovalDate;

  const initialDate = skipDate ?? new Date();
  const weekdaySelection: boolean[] = [];
  const monthSelection: boolean[] = [];

  const initialSimpleDate = new SimpleDate(initialDate);
  weekdaySelection[initialSimpleDate.weekday] = true;
  monthSelection[initialSimpleDate.month - 1] = true;

  if (!team || team === "org") {
    return (
      <p>Cannot create reoccurring items without first selecting a team.</p>
    );
  }

  return (
    <div className="w-full">
      <Formik
        initialValues={
          {
            interval: "yearly",
            startDate: initialDate,
            skip: 1,
            weekdaySelection,
            monthSelection,
            nthWeekday: false,
            weekdayIndex: 1,
            weekdayDay: 1,
            mode: "count",
            count: 1,
            until: new Date(),
          } satisfies RecurrenceSettings as RecurrenceSettings
        }
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          const occurrences = computeReoccurrences({
            values,
            skipDate,
            maxRecurrences,
          });

          // compute time offset into day (just in case agenda items ever support time for approval date)
          const offset = skipDate
            ? skipDate.getTime() - new SimpleDate(skipDate).date.getTime()
            : 0;

          console.log(occurrences);

          for (const date of occurrences) {
            const copiedAgendaItem = agendaTemplateService.getCopiedAgendaItem(
              {
                ...agendaItem,
                requestedApprovalDate: new Date(date.getTime() + offset),
              },
              team.id
            );

            const newAgendaItem = await agendaItemRepo.create(
              tenantId,
              copiedAgendaItem
            );
            trackNew("agendaItem", newAgendaItem);
          }

          console.log("success");

          handleComplete();
        }}
      >
        {({
          values,
          handleSubmit,
          handleBlur,
          handleChange,
          setFieldValue,
          isValid,
          isSubmitting,
        }) => {
          const occurrences = computeReoccurrences({
            values,
            skipDate,
            maxRecurrences,
          });

          const footer = (
            <>
              <Button
                type="button"
                disabled={isSubmitting}
                variant="destructive"
                onClick={() => handleCancel()}
              >
                Cancel
              </Button>
              <div className="flex-1" />
              <Button
                type="submit"
                disabled={!isValid || isSubmitting || !occurrences.length}
              >
                Create {occurrences.length} copies
              </Button>
            </>
          );

          const form = (
            <div className="flex flex-col gap-y-4">
              <p className="mt-2">
                Configure schedule for copying agenda item, '
                <strong>{agendaItem.name}</strong>':
              </p>
              <div className="flex flex-col gap-y-2">
                <DatePicker
                  id="startDate"
                  label="Starts"
                  date={values.startDate}
                  onDateChange={(value) => {
                    if (!value) return;

                    const [weekdays, months] = tweakSelections(
                      values.startDate,
                      value,
                      values.weekdaySelection,
                      values.monthSelection
                    );

                    setFieldValue("startDate", value);
                    if (value && value > values.until) {
                      setFieldValue("until", value);
                    }
                    setFieldValue("weekdaySelection", weekdays);
                    setFieldValue("monthSelection", months);
                  }}
                />
              </div>
              <div>
                <p className="text-slate-800 text-base leading-5">
                  Recurs Every
                </p>
                <div className="flex flex-row gap-x-1">
                  <Input
                    id="skip"
                    name="skip"
                    containerStyle="w-0 grow"
                    onBlur={(evt) => {
                      setFieldValue("skip", wholeNumber(evt.target.value));
                      handleBlur(evt);
                    }}
                    value={values.skip}
                    onChange={handleChange}
                  />
                  <SelectInput
                    value={values.interval}
                    onValueChange={(value) => {
                      setFieldValue("interval", value);
                    }}
                    containerStyle="w-0 grow-[3]"
                    options={intervalOptions.map((interval) => (
                      <SelectItem key={interval} value={interval}>
                        {intervalNames[interval]}
                      </SelectItem>
                    ))}
                  />
                </div>
              </div>
              {values.interval === "weekly" ? (
                <div>
                  <WeekdayPicker
                    id="weekdaySelection"
                    label="Repeat On &nbsp; (Note: multiple selected weekdays below will create multiple copies within each week period.)"
                    value={values.weekdaySelection}
                    onChange={(value) => {
                      setFieldValue("weekdaySelection", value);
                    }}
                  />
                </div>
              ) : values.interval === "yearly" ? (
                <div>
                  <MonthPicker
                    id="monthSelection"
                    label="Repeat On &nbsp; (Note: multiple selected months below will create multiple copies within each year period.)"
                    value={values.monthSelection}
                    onChange={(value) => {
                      setFieldValue("monthSelection", value);
                    }}
                  />
                </div>
              ) : null}
              {(values.interval === "monthly" ||
                values.interval === "yearly") && (
                <div>
                  <CheckboxInput
                    orientation="horizontal"
                    label="nth Weekday"
                    checked={values.nthWeekday}
                    onCheckedChange={(value) =>
                      setFieldValue("nthWeekday", value)
                    }
                  />
                  {values.nthWeekday && (
                    <>
                      <p className="text-slate-800 text-base leading-5 mt-4">
                        On the...
                      </p>
                      <div className="flex flex-row gap-x-1 items-center">
                        <SelectInput
                          containerStyle="w-0 grow"
                          value={"" + values.weekdayIndex}
                          onValueChange={(value) => {
                            setFieldValue("weekdayIndex", +value);
                          }}
                          options={weekdayIndexOptions.map((ind) => (
                            <SelectItem key={ind} value={ind}>
                              {weekdayIndexNames[ind]}
                            </SelectItem>
                          ))}
                        />
                        <SelectInput
                          containerStyle="w-0 grow-[3]"
                          value={"" + values.weekdayDay}
                          onValueChange={(value) => {
                            setFieldValue("weekdayDay", +value);
                          }}
                          options={weekdays.map((wd, i) => (
                            <SelectItem key={i} value={"" + i}>
                              {wd}
                            </SelectItem>
                          ))}
                        />
                        <p className="ml-1">Of the month</p>
                      </div>
                    </>
                  )}
                </div>
              )}
              <div>
                <p className="text-slate-800 text-base leading-5">Ends</p>
                <div className="flex flex-row gap-x-1">
                  <SelectInput
                    containerStyle="w-0 grow"
                    value={values.mode}
                    onValueChange={(value) => {
                      setFieldValue("mode", value);
                    }}
                    options={[
                      <SelectItem key="until" value="until">
                        On
                      </SelectItem>,
                      <SelectItem key="count" value="count">
                        After
                      </SelectItem>,
                    ]}
                  />
                  <div className="w-0 grow-[2] flex flex-row gap-x-2 items-center">
                    {values.mode === "until" ? (
                      <DatePicker
                        id="until"
                        className="grow mt-1.5"
                        date={values.until}
                        onDateChange={(value) => {
                          setFieldValue("until", value);
                        }}
                        calendarProps={{
                          fromDate: values.startDate,
                        }}
                      />
                    ) : (
                      <>
                        <Input
                          id="count"
                          name="count"
                          containerStyle="grow"
                          onBlur={(evt) => {
                            setFieldValue(
                              "count",
                              wholeNumber(evt.target.value, maxRecurrences)
                            );
                            handleBlur(evt);
                          }}
                          value={values.count}
                          onChange={handleChange}
                        />
                        <p className="mt-1.5">Occurrences</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div>
                <p className="text-slate-800 text-base leading-5">
                  Recurrence Schedule Preview:
                </p>
                {agendaItem?.requestedApprovalDate && (
                  <p>
                    (Existing agenda item's date,{" "}
                    {agendaItem.requestedApprovalDate.toDateString()}, will be
                    skipped)
                  </p>
                )}
              </div>
              <ReoccurrencePreview occurrences={occurrences} />
              {!withinDialog && (
                <div className="flex flex-row justify-between">{footer}</div>
              )}
            </div>
          );

          return (
            <div>
              <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      Confirm Creation of {occurrences.length} Item(s)
                    </DialogTitle>
                  </DialogHeader>
                  <DialogBody>
                    <p className="my-2">
                      Please confirm creation of {occurrences.length} item(s).
                    </p>
                    <p>
                      <strong>Notice: This action cannot be undone.</strong> If
                      you need to revert this change, you will need to manually
                      delete all {occurrences.length} item(s). The copying
                      process may also take a long time. Do not refresh or leave
                      the page while it loads.
                    </p>
                    <p className="mt-5 font-bold">Agenda Item being copied:</p>
                    <div className="mt-4 mb-6 mx-3">
                      <p>{agendaItem.name}</p>
                      <p>{agendaItem.description}</p>
                      <ul>
                        {agendaItem.documents?.map((doc) => (
                          <li key={doc.id}>{doc.name}</li>
                        ))}
                      </ul>
                    </div>
                    <p className="mt-5 font-bold">Schedule:</p>
                    <div className="mt-4 mb-6 mx-3">
                      <p>
                        Every {values.skip}{" "}
                        {intervalNames[values.interval].toLowerCase()}
                      </p>
                      <p>
                        Copies start from {occurrences[0]?.toDateString()} and
                        run to{" "}
                        {occurrences[occurrences.length - 1]?.toDateString()}
                      </p>
                      <p>{occurrences.length} total occurrences.</p>
                    </div>
                  </DialogBody>
                  <DialogFooter className="justify-between">
                    <Button
                      type="button"
                      disabled={isSubmitting}
                      onClick={() => {
                        setSubmission(undefined);
                        setConfirmOpen(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <div className="flex-1" />
                    <Button
                      type="button"
                      disabled={isSubmitting}
                      variant="destructive"
                      onClick={() => {
                        handleSubmit(submission);
                      }}
                    >
                      Confirm
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <form
                onSubmit={(evt) => {
                  setSubmission(evt);
                  setConfirmOpen(true);
                  evt.preventDefault();
                }}
              >
                {withinDialog ? (
                  <>
                    <DialogBody>{form}</DialogBody>
                    <DialogFooter>{footer}</DialogFooter>
                  </>
                ) : (
                  form
                )}
              </form>
            </div>
          );
        }}
      </Formik>
    </div>
  );
};
